import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { fetchMailSettings, saveMailSettings, sendMailTest } from '../adminApi'
import { useServerDraft } from '../../hooks/useServerDraft'
import { newRequestId } from '../../core/api/http'
import { errorMessage } from '../../core/errors'
import { mailChecks, seriesChecks, withSalonFooter } from '../../core/rules/mail'
import { useAdmin } from '../AdminContext'
import type { MailBrand, MailSeries, MailSettings, MailTemplate } from '../../core/types/mail'

const toDraft = (s: MailSettings) => s

/**
 * De automatische mails, reeksen en huisstijl: één blok naar de server (GET/PUT /admin/mails),
 * met een eigen Opslaan (niet de conceptbalk van het beheer). Nooit optimistisch. Naam, adres en
 * Instagram in de voettekst gaan altijd mee uit Instellingen › Salon & contact.
 */
export function useMailSettings() {
  const { site } = useAdmin()
  const salon = site.salon
  const persist = useCallback((draft: MailSettings) => saveMailSettings({ ...draft, brand: withSalonFooter(draft.brand, salon) }), [salon])
  const s = useServerDraft({
    load: fetchMailSettings,
    toDraft,
    persist,
    savedTitle: 'Mails opgeslagen',
    savedText: 'De volgende mail gaat met deze tekst de deur uit.',
    conflictSubject: 'De mails',
  })
  const { edit, dirty, current } = s

  const setTemplate = useCallback(
    (key: string, patch: Partial<MailTemplate>) => edit((m) => ({ ...m, templates: m.templates.map((t) => (t.key === key ? { ...t, ...patch } : t)) })),
    [edit],
  )
  const setSeries = useCallback(
    (key: string, patch: Partial<MailSeries>) => edit((m) => ({ ...m, series: m.series.map((t) => (t.key === key ? { ...t, ...patch } : t)) })),
    [edit],
  )
  const setBrand = useCallback((patch: Partial<MailBrand>) => edit((m) => ({ ...m, brand: { ...m.brand, ...patch } })), [edit])

  /** Rode punten over alle mails samen: opslaan kan pas als dit 0 is. */
  const errors = useMemo(() => {
    if (!current) return 0
    const all = [...current.templates.flatMap((m) => mailChecks(m)), ...current.series.flatMap((m) => seriesChecks(m))]
    return all.filter((c) => c.level === 'error').length + (withSalonFooter(current.brand, salon).footer.address.trim() ? 0 : 1)
  }, [current, salon])

  /** Testmail naar jezelf. Met niet-opgeslagen wijzigingen: eerst opslaan, want de server stuurt de opgeslagen tekst. */
  const sendTest = useCallback(
    async (key: string) => {
      if (dirty) {
        toast('Sla eerst op', { description: 'De testmail gebruikt de opgeslagen tekst.' })
        return false
      }
      try {
        await sendMailTest(key, { requestId: newRequestId() })
        toast.success('Testmail verstuurd', { description: 'Naar je eigen adres, met voorbeeldwaarden ingevuld.' })
        return true
      } catch (err) {
        toast.error('Testmail versturen lukte niet', { description: errorMessage(err) ?? 'Probeer het zo nog eens.' })
        return false
      }
    },
    [dirty],
  )

  return { settings: current, status: s.status, reload: s.reload, dirty, saving: s.saving, errors, setTemplate, setSeries, setBrand, save: s.save, discard: s.discard, sendTest }
}

export type MailSettingsState = ReturnType<typeof useMailSettings>
